/*!
 * artmie-wishlist.js v3 — localStorage wishlist + cart integration.
 *
 * Click on product card heart → saves product (with default variant) to localStorage.
 * On /pages/wishlist the grid shows each item with:
 *   - image, title, price (live-fetched if needed)
 *   - variant dropdown (if product has variants)
 *   - qty input
 *   - single "Add to cart" button
 * Plus a "Add all to cart" bulk button at the top.
 *
 * Safety: NO MutationObserver, no polling. Paints only on
 *   DOMContentLoaded / click / storage / shopify:section:load.
 * Re-entrancy guard on paint(). Idempotent DOM writes.
 */
(function () {
  'use strict';
  try { console.log('%c[ARTMIE WL v4] loaded — '+location.host, 'background:#feb21f;color:#000;padding:2px 6px;border-radius:3px;font-weight:bold'); } catch(e){}
  var KEY = 'artmie_wishlist_v1';
  var painting = false;
  var productCache = {}; // handle -> full product JSON (variants list)

  // ---------- storage ----------
  function load() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function save(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    paint();
  }
  function has(handle) { return load().some(function (i) { return i.handle === handle; }); }
  function toggle(item) {
    var list = load();
    var idx = list.findIndex(function (i) { return i.handle === item.handle; });
    if (idx >= 0) list.splice(idx, 1);
    else list.unshift({
      handle: item.handle,
      title: item.title,
      url: item.url,
      image: item.image,
      price: item.price,
      brand: item.brand || '',
      rating: item.rating || '',
      ratingCount: item.ratingCount || '',
      variantId: item.variantId || null,
      qty: 1,
      added: Date.now()
    });
    save(list);
    return idx < 0;
  }
  function updateItem(handle, patch) {
    var list = load();
    var idx = list.findIndex(function (i) { return i.handle === handle; });
    if (idx < 0) return;
    list[idx] = Object.assign({}, list[idx], patch);
    save(list);
  }

  // ---------- product fetch ----------
  // Strip Shopify's bogus "Default Title" placeholder variant when real named variants exist.
  function stripDefaultVariant(p) {
    if (!p || !p.variants || p.variants.length <= 1) return p;
    var real = p.variants.filter(function (v) {
      if (v.title === 'Default Title' || v.title === 'Default') return false;
      if (v.options && v.options.length === 1 && (v.options[0] === 'Default Title' || v.options[0] === 'Default')) return false;
      return true;
    });
    if (real.length > 0) p.variants = real;
    return p;
  }
  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p) { stripDefaultVariant(p); productCache[handle] = p; } return p; })
      .catch(function () { return null; });
  }

  // ---------- variant image resolver ----------
  function extractSrc(v) {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (v.src) return v.src;
    if (v.url) return v.url;
    if (v.preview_image && v.preview_image.src) return v.preview_image.src;
    if (v.image && (v.image.src || v.image.url)) return v.image.src || v.image.url;
    return '';
  }
  function variantImage(product, variant) {
    // 1. Variant's own featured_image (may be object or string)
    var src = extractSrc(variant && variant.featured_image);
    if (src) return sizeImg(src, 200);
    // 2. featured_media
    src = extractSrc(variant && variant.featured_media);
    if (src) return sizeImg(src, 200);
    // 3. Product featured_image
    src = extractSrc(product && product.featured_image);
    if (src) return sizeImg(src, 200);
    // 4. Product.media[0]
    if (product && product.media && product.media[0]) {
      src = extractSrc(product.media[0]);
      if (src) return sizeImg(src, 200);
    }
    // 5. Product.images[0]
    if (product && product.images && product.images[0]) {
      src = extractSrc(product.images[0]);
      if (src) return sizeImg(src, 200);
    }
    return '';
  }
  function sizeImg(url, size) {
    if (!url) return '';
    // Ensure protocol for protocol-relative //cdn.shopify.com URLs
    if (url.indexOf('//') === 0) url = 'https:' + url;
    // Shopify CDN: inject _Nx before extension
    return url.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?.*)?$/i, '_' + size + 'x$1$2');
  }

  // ---------- price formatting ----------
  // Shopify Ajax API returns prices as integer in cents (e.g. 25900 = 259.00 MKD).
  // Guard against decimal strings in case the API serves them differently on some surfaces.
  function formatMoney(raw) {
    if (raw == null) return '';
    var n;
    if (typeof raw === 'string') {
      // "259.00" — already a decimal denar amount, don't divide
      if (raw.indexOf('.') >= 0 || raw.indexOf(',') >= 0) {
        n = parseFloat(raw.replace(',', '.'));
      } else {
        // Pure integer string — treat as cents
        n = parseInt(raw, 10) / 100;
      }
    } else if (typeof raw === 'number') {
      // Integer cents
      n = raw / 100;
    } else {
      return '';
    }
    if (!isFinite(n)) return '';
    // Macedonia: denar, no decimals, "." as thousands separator
    var fixed = Math.round(n).toString();
    return fixed.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' ден';
  }

  // Clean the "Default Title" placeholder that Shopify emits for single-option variants
  function cleanVariantTitle(t, productTitle) {
    if (!t) return productTitle || '';
    var s = String(t).trim();
    if (s === 'Default Title' || s === 'Default') return productTitle || '';
    // Some products use "Default Title / X" etc — strip the default prefix
    s = s.replace(/^Default Title\s*\/\s*/, '').replace(/\s*\/\s*Default Title$/, '');
    return s || (productTitle || '');
  }

  var HEART_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
  function injectCardHearts() {
    var stuck = document.querySelectorAll('.product-card-actions [data-wishlist-toggle]');
    for (var s = 0; s < stuck.length; s++) {
      var h = stuck[s];
      var card = h.closest('product-card, .product-card');
      if (card && h.parentElement !== card) {
        h.classList.remove('product-card-actions__button', 'product-card-actions__button--wishlist');
        h.classList.add('artmie-wl-injected-heart');
        h.innerHTML = HEART_SVG;
        if (getComputedStyle(card).position === 'static') card.style.position = 'relative';
        card.appendChild(h);
      }
    }
    var cards = document.querySelectorAll('product-card, .product-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.querySelector('[data-wishlist-toggle]')) continue;
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) continue;
      var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (!m) continue;
      var handle = m[1];
      var titleEl = card.querySelector('.product-card__title, .product-title, h3, h2, [class*="title"]');
      var title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';
      var img = card.querySelector('img');
      var image = img ? (img.currentSrc || img.src || '') : '';
      var priceEl = card.querySelector('.price__regular, .price-item--regular, .price, [class*="price"]');
      var price = priceEl ? priceEl.textContent.trim().replace(/\s+/g, ' ') : '';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'artmie-wl-injected-heart';
      btn.setAttribute('data-wishlist-toggle', '');
      btn.setAttribute('data-wishlist-handle', handle);
      btn.setAttribute('data-wishlist-title', title);
      btn.setAttribute('data-wishlist-url', '/products/' + handle);
      btn.setAttribute('data-wishlist-image', image);
      btn.setAttribute('data-wishlist-price', price);
      btn.setAttribute('aria-label', 'Wishlist');
      btn.setAttribute('aria-pressed', 'false');
      btn.innerHTML = HEART_SVG;
      if (getComputedStyle(card).position === 'static') card.style.position = 'relative';
      card.appendChild(btn);
    }
  }

  function injectPdpHeart() {
    var pathMatch = (location.pathname || '').match(/^\/products\/([^/?#]+)/);
    if (!pathMatch) return;
    var handle = pathMatch[1];
    if (document.querySelector('.artmie-wl-pdp-heart')) return;
    var atcWrap = document.querySelector('.artmie-pdp__atc-wrap, add-to-cart-component:not(product-card add-to-cart-component)');
    var formRow = atcWrap && atcWrap.closest('.artmie-pdp__form-row, .product-form, .product__form, [class*="form-row"], form');
    var anchor = formRow || atcWrap;
    if (!anchor || !atcWrap) return;
    var titleEl = document.querySelector('h1');
    var title = (titleEl && titleEl.textContent || document.title || '').trim().replace(/\s*[–|]\s*[^|–]+$/, '').slice(0, 200);
    var img = document.querySelector('main img, [class*="gallery"] img, [class*="product"] img');
    var image = img ? (img.currentSrc || img.src || '') : '';
    var priceEl = document.querySelector('[class*="price__current"], [class*="price-item--regular"], .price__regular, [class*="price"]:not(:empty)');
    var price = priceEl ? priceEl.textContent.trim().replace(/\s+/g,' ').slice(0, 50) : '';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'artmie-wl-pdp-heart';
    btn.setAttribute('data-wishlist-toggle', '');
    btn.setAttribute('data-wishlist-handle', handle);
    btn.setAttribute('data-wishlist-title', title);
    btn.setAttribute('data-wishlist-url', '/products/' + handle);
    btn.setAttribute('data-wishlist-image', image);
    btn.setAttribute('data-wishlist-price', price);
    btn.setAttribute('aria-label', 'Wishlist');
    btn.setAttribute('aria-pressed', 'false');
    btn.innerHTML = HEART_SVG;
    if (atcWrap.parentElement) {
      atcWrap.parentElement.insertBefore(btn, atcWrap.nextSibling);
    }
  }

  // ---------- paint ----------
  function paint() {
    if (painting) return;
    painting = true;
    try {
      injectCardHearts();
      injectPdpHeart();
      var list = load();
      var saved = {};
      for (var i = 0; i < list.length; i++) saved[list[i].handle] = true;

      // Heart buttons on cards
      var buttons = document.querySelectorAll('[data-wishlist-toggle]');
      for (var j = 0; j < buttons.length; j++) {
        var btn = buttons[j];
        var handle = btn.getAttribute('data-wishlist-handle');
        if (!handle) continue;
        var wanted = !!saved[handle];
        if (wanted !== btn.classList.contains('is-wished')) btn.classList.toggle('is-wished', wanted);
        var prev = btn.getAttribute('aria-pressed') === 'true';
        if (wanted !== prev) btn.setAttribute('aria-pressed', wanted ? 'true' : 'false');
      }

      // Count badges
      var n = list.length;
      var counters = document.querySelectorAll('[data-wishlist-count]');
      for (var k = 0; k < counters.length; k++) {
        var el = counters[k];
        var newText = String(n);
        if (el.textContent !== newText) el.textContent = newText;
        var zero = n === 0;
        if (zero !== el.classList.contains('is-zero')) el.classList.toggle('is-zero', zero);
      }

      // Wishlist page
      var host = document.getElementById('artmie-wishlist-page');
      if (host) renderPage(host, list);
    } finally {
      painting = false;
    }
  }

  // ---------- wishlist page renderer ----------
  function renderPage(host, list) {
    if (!list.length) {
      var emptyHtml = '<div class="artmie-wishlist-empty"><p>' +
        escapeHtml(host.getAttribute('data-empty-text') || 'Вашата листа на желби е празна.') +
        '</p><a href="/collections/all" class="button button-primary">' +
        escapeHtml(host.getAttribute('data-browse-text') || 'Разгледај производи') +
        '</a></div>';
      if (host.innerHTML !== emptyHtml) host.innerHTML = emptyHtml;
      return;
    }

    var frags = [];
    // Bulk header
    frags.push(
      '<div class="artmie-wishlist-toolbar">' +
        '<div class="artmie-wishlist-toolbar__count">' + list.length + ' производи</div>' +
        '<button type="button" class="artmie-wishlist-addall button button-primary" data-wishlist-add-all>' +
          'Додај ги сите во кошничка' +
        '</button>' +
      '</div>'
    );
    frags.push('<div class="artmie-wishlist-grid">');
    for (var i = 0; i < list.length; i++) {
      frags.push(renderCard(list[i]));
    }
    frags.push('</div>');
    var newHtml = frags.join('');
    if (host.innerHTML !== newHtml) host.innerHTML = newHtml;

    // Hydrate variant dropdowns + prices from product JSON
    list.forEach(function (item) { hydrateCard(item.handle); });
  }

  function renderCard(it) {
    var imgHtml = it.image
      ? '<img src="' + escapeAttr(it.image) + '" alt="" loading="lazy" class="artmie-wishlist-card__image">'
      : '<div class="artmie-wishlist-card__image artmie-wishlist-card__image--placeholder"></div>';
    var priceHtml = it.price
      ? '<div class="artmie-wishlist-card__price" data-wishlist-card-price>' + escapeHtml(it.price) + '</div>'
      : '<div class="artmie-wishlist-card__price" data-wishlist-card-price>…</div>';
    var brandHtml = it.brand
      ? '<div class="artmie-wishlist-card__brand">' + escapeHtml(it.brand) + '</div>'
      : '<div class="artmie-wishlist-card__brand" data-wishlist-card-brand></div>';
    var ratingHtml = '<div class="artmie-wishlist-card__rating" data-wishlist-card-rating>' + renderStars(it.rating, it.ratingCount) + '</div>';

    var variantHtml = '<div class="artmie-wishlist-card__variants" data-wishlist-card-variants></div>';
    var qty = Math.max(1, parseInt(it.qty || 1, 10) || 1);

    return (
      '<div class="artmie-wishlist-card" data-wishlist-item="' + escapeAttr(it.handle) + '">' +
        '<button type="button" class="artmie-wishlist-card__remove" ' +
          'data-wishlist-toggle data-wishlist-handle="' + escapeAttr(it.handle) + '" ' +
          'data-wishlist-title="' + escapeAttr(it.title) + '" aria-label="Отстрани">' +
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M18 6L6 18M6 6l12 12"/></svg>' +
        '</button>' +
        '<a href="/products/' + escapeAttr(it.handle) + '" class="artmie-wishlist-card__link">' +
          imgHtml +
          brandHtml +
          ratingHtml +
          '<h3 class="artmie-wishlist-card__title">' + escapeHtml(it.title) + '</h3>' +
        '</a>' +
        priceHtml +
        variantHtml +
        '<div class="artmie-wishlist-card__controls">' +
          '<div class="artmie-wishlist-card__qty">' +
            '<button type="button" class="artmie-qty-btn" data-wishlist-qty-dec aria-label="Намали">−</button>' +
            '<input type="number" min="1" max="99" value="' + qty + '" ' +
              'class="artmie-qty-input" data-wishlist-qty-input data-wishlist-handle="' + escapeAttr(it.handle) + '">' +
            '<button type="button" class="artmie-qty-btn" data-wishlist-qty-inc aria-label="Зголеми">+</button>' +
          '</div>' +
          '<button type="button" class="artmie-wishlist-card__add button button-primary" ' +
            'data-wishlist-add data-wishlist-handle="' + escapeAttr(it.handle) + '">' +
            'Во кошничка' +
          '</button>' +
        '</div>' +
      '</div>'
    );
  }

  function hydrateCard(handle) {
    fetchProduct(handle).then(function (p) {
      var card = document.querySelector('[data-wishlist-item="' + cssEsc(handle) + '"]');
      if (!card) return;
      if (!p) {
        // Product no longer exists on storefront — mark unavailable
        card.classList.add('is-unavailable');
        var priceEl = card.querySelector('[data-wishlist-card-price]');
        if (priceEl) priceEl.innerHTML = '<span style="color:#a33;font-weight:500;font-size:13px">Производот веќе не е достапен</span>';
        var addBtn = card.querySelector('[data-wishlist-add]');
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Недостапно'; }
        var varsEl = card.querySelector('[data-wishlist-card-variants]');
        if (varsEl) varsEl.innerHTML = '';
        return;
      }
      var item = load().find(function (i) { return i.handle === handle; });
      if (!item) return;

      var priceEl = card.querySelector('[data-wishlist-card-price]');
      var varsEl = card.querySelector('[data-wishlist-card-variants]');

      // Variants picker — visual button that opens the modal with thumbnails
      if (p.variants && p.variants.length > 1) {
        var currentId = item.variantId || p.variants[0].id;
        var currentVariant = p.variants.find(function (v) { return String(v.id) === String(currentId); }) || p.variants[0];
        var thumb = variantImage(p, currentVariant);
        var thumbHtml = thumb
          ? '<img src="' + escapeAttr(thumb) + '" alt="" class="artmie-wishlist-card__variant-thumb">'
          : '<span class="artmie-wishlist-card__variant-thumb artmie-wishlist-card__variant-thumb--placeholder"></span>';
        if (varsEl) {
          varsEl.innerHTML =
            '<label class="artmie-wishlist-card__variant-label">Варијанта:</label>' +
            '<button type="button" class="artmie-wishlist-card__variant-picker" ' +
              'data-wishlist-change-variant data-wishlist-handle="' + escapeAttr(handle) + '">' +
              thumbHtml +
              '<span class="artmie-wishlist-card__variant-picker-name">' + escapeHtml(cleanVariantTitle(currentVariant.title, p.title)) + '</span>' +
              '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>' +
            '</button>';
        }
      } else if (varsEl) {
        varsEl.innerHTML = '';
      }

      // Price — current variant price
      if (priceEl) {
        var variantId = item.variantId || (p.variants && p.variants[0] && p.variants[0].id);
        var variant = (p.variants || []).find(function (v) { return String(v.id) === String(variantId); }) || p.variants[0];
        if (variant) priceEl.textContent = formatMoney(variant.price);
      }

      // Refresh image from CURRENT product data — the URL stored when the user saved
      // the item may now 404 if the merchant re-uploaded the image.
      var imgEl = card.querySelector('img.artmie-wishlist-card__image');
      var freshVariantId = item.variantId || (p.variants && p.variants[0] && p.variants[0].id);
      var freshVariant = (p.variants || []).find(function (v) { return String(v.id) === String(freshVariantId); }) || p.variants[0];
      var freshImg = variantImage(p, freshVariant);
      if (freshImg && imgEl) {
        if (!imgEl.complete || imgEl.naturalWidth === 0 || imgEl.src !== freshImg) {
          imgEl.onload = function () { updateItem(handle, { image: freshImg }); };
          imgEl.onerror = function () { /* leave; renderPage will retry next paint */ };
          imgEl.src = freshImg;
        }
      } else if (freshImg && !imgEl) {
        var place = card.querySelector('.artmie-wishlist-card__image--placeholder');
        if (place) {
          var newImg = document.createElement('img');
          newImg.className = 'artmie-wishlist-card__image';
          newImg.alt = '';
          newImg.loading = 'lazy';
          newImg.onload = function () { updateItem(handle, { image: freshImg }); };
          newImg.src = freshImg;
          place.parentNode.replaceChild(newImg, place);
        }
      }
    });
  }

  // ---------- cart add ----------
  function addToCart(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Грешка'); });
      return r.json();
    });
  }

  function handleAddAll(e) {
    e.preventDefault();
    e.stopPropagation();
    var btn = e.currentTarget;
    btn.disabled = true;
    btn.textContent = 'Додавам…';
    var list = load();
    var chain = Promise.resolve();
    var added = 0, failed = 0;
    list.forEach(function (item) {
      chain = chain.then(function () {
        var card = document.querySelector('[data-wishlist-item="' + cssEsc(item.handle) + '"]');
        var qtyInput = card && card.querySelector('[data-wishlist-qty-input]');
        var selEl = card && card.querySelector('[data-wishlist-variant]');
        var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : (item.qty || 1);
        return fetchProduct(item.handle).then(function (p) {
          var vid = (selEl && selEl.value) || item.variantId || (p && p.variants && p.variants[0] && p.variants[0].id);
          if (!vid) { failed++; return; }
          return addToCart(vid, qty).then(function () { added++; }).catch(function () { failed++; });
        });
      });
    });
    chain.then(function () {
      btn.disabled = false;
      btn.textContent = failed ? 'Додадено ' + added + ' (грешки: ' + failed + ')' : 'Сите додадени ✓';
      setTimeout(function () {
        // Refresh cart count if theme exposes a cart drawer
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        btn.textContent = 'Додај ги сите во кошничка';
      }, 2500);
    });
  }

  function handleAddOne(e) {
    e.preventDefault();
    e.stopPropagation();
    var btn = e.currentTarget;
    var handle = btn.getAttribute('data-wishlist-handle');
    var card = document.querySelector('[data-wishlist-item="' + cssEsc(handle) + '"]');
    var qtyInput = card && card.querySelector('[data-wishlist-qty-input]');
    var selEl = card && card.querySelector('[data-wishlist-variant]');
    var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    var item = load().find(function (i) { return i.handle === handle; });
    btn.disabled = true;
    var orig = btn.textContent;
    btn.textContent = 'Додавам…';
    fetchProduct(handle).then(function (p) {
      var vid = (selEl && selEl.value) || (item && item.variantId) || (p && p.variants && p.variants[0] && p.variants[0].id);
      if (!vid) throw new Error('нема варијанта');
      return addToCart(vid, qty);
    }).then(function () {
      btn.textContent = 'Додадено ✓';
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      setTimeout(function () { btn.disabled = false; btn.textContent = orig; }, 2000);
    }).catch(function (err) {
      btn.textContent = 'Грешка';
      setTimeout(function () { btn.disabled = false; btn.textContent = orig; }, 2500);
    });
  }

  function handleVariantChange(e) {
    var sel = e.target.closest ? e.target.closest('[data-wishlist-variant]') : null;
    if (!sel) return;
    var handle = sel.getAttribute('data-wishlist-handle');
    var vid = sel.value;
    updateItem(handle, { variantId: vid });
    // Update price display for this card
    var card = sel.closest('[data-wishlist-item]');
    if (card) {
      var priceEl = card.querySelector('[data-wishlist-card-price]');
      fetchProduct(handle).then(function (p) {
        var variant = (p && p.variants || []).find(function (v) { return String(v.id) === String(vid); });
        if (variant && priceEl) priceEl.textContent = formatMoney(variant.price);
      });
    }
  }

  function handleQtyBtn(e, dir) {
    var btn = e.target.closest ? e.target.closest(dir > 0 ? '[data-wishlist-qty-inc]' : '[data-wishlist-qty-dec]') : null;
    if (!btn) return;
    e.preventDefault();
    var input = btn.parentElement.querySelector('[data-wishlist-qty-input]');
    if (!input) return;
    var cur = Math.max(1, parseInt(input.value, 10) || 1);
    var next = Math.max(1, Math.min(99, cur + dir));
    if (next !== cur) {
      input.value = next;
      updateItem(input.getAttribute('data-wishlist-handle'), { qty: next });
    }
  }

  function handleQtyInput(e) {
    var input = e.target.closest ? e.target.closest('[data-wishlist-qty-input]') : null;
    if (!input) return;
    var v = Math.max(1, Math.min(99, parseInt(input.value, 10) || 1));
    input.value = v;
    updateItem(input.getAttribute('data-wishlist-handle'), { qty: v });
  }

  // ---------- variant-picker modal ----------
  // mode: 'save' (default) toggles save | 'change' updates existing wishlist item's variant
  function openVariantPicker(item, product, mode) {
    mode = mode || 'save';
    closeVariantPicker();
    var overlay = document.createElement('div');
    overlay.className = 'artmie-wl-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML =
      '<div class="artmie-wl-modal__backdrop" data-wl-modal-close></div>' +
      '<div class="artmie-wl-modal__dialog">' +
        '<button type="button" class="artmie-wl-modal__close" data-wl-modal-close aria-label="Затвори">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
        '</button>' +
        '<h2 class="artmie-wl-modal__title">Избери варијанта</h2>' +
        '<p class="artmie-wl-modal__subtitle">' + escapeHtml(item.title) + '</p>' +
        '<ul class="artmie-wl-modal__variants">' +
          product.variants.slice().sort(function (a, b) {
            // In-stock first, then out-of-stock; preserve original order within each group
            if (a.available === b.available) return 0;
            return a.available ? -1 : 1;
          }).map(function (v) {
            var price = formatMoney(v.price);
            var imgSrc = variantImage(product, v);
            var imgHtml = imgSrc
              ? '<img class="artmie-wl-modal__variant-img" src="' + escapeAttr(imgSrc) + '" alt="" loading="lazy">'
              : '<span class="artmie-wl-modal__variant-img artmie-wl-modal__variant-img--placeholder"></span>';
            return '<li class="artmie-wl-modal__variant' + (v.available ? '' : ' is-unavailable') + '">' +
              '<button type="button" class="artmie-wl-modal__variant-btn" ' +
                'data-wl-pick-variant="' + v.id + '" ' + (v.available ? '' : 'disabled') + '>' +
                imgHtml +
                '<span class="artmie-wl-modal__variant-body">' +
                  '<span class="artmie-wl-modal__variant-title">' + escapeHtml(cleanVariantTitle(v.title, product.title)) + '</span>' +
                  '<span class="artmie-wl-modal__variant-meta">' +
                    '<span class="artmie-wl-modal__variant-price">' + price + '</span>' +
                    (v.available ? '' : '<span class="artmie-wl-modal__variant-oos">нема залиха</span>') +
                  '</span>' +
                '</span>' +
              '</button>' +
            '</li>';
          }).join('') +
        '</ul>' +
      '</div>';
    document.body.appendChild(overlay);
    document.body.classList.add('artmie-wl-modal-open');
    // Focus first enabled button
    var firstBtn = overlay.querySelector('.artmie-wl-modal__variant-btn:not([disabled])');
    if (firstBtn) firstBtn.focus();

    // Pick variant handler
    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-wl-modal-close]')) { closeVariantPicker(); return; }
      var pickBtn = e.target.closest('[data-wl-pick-variant]');
      if (pickBtn) {
        var vid = pickBtn.getAttribute('data-wl-pick-variant');
        var variant = product.variants.find(function (v) { return String(v.id) === String(vid); });
        if (!variant) return;
        var patch = {
          variantId: vid,
          price: formatMoney(variant.price),
          image: variantImage(product, variant) || item.image
        };
        if (mode === 'change') {
          // Update existing item without toggling
          updateItem(item.handle, patch);
        } else {
          var full = Object.assign({}, item, patch);
          toggle(full);
        }
        closeVariantPicker();
      }
    });
    document.addEventListener('keydown', escCloseModal);
  }
  function closeVariantPicker() {
    var existing = document.querySelector('.artmie-wl-modal');
    if (existing) existing.remove();
    document.body.classList.remove('artmie-wl-modal-open');
    document.removeEventListener('keydown', escCloseModal);
  }
  function escCloseModal(e) { if (e.key === 'Escape') closeVariantPicker(); }

  // ---------- click dispatch ----------
  function onClick(e) {
    // bulk add-all
    var all = e.target.closest ? e.target.closest('[data-wishlist-add-all]') : null;
    if (all) { handleAddAll({ preventDefault: function () {}, stopPropagation: function () {}, currentTarget: all }); return; }
    // per-item add
    var add = e.target.closest ? e.target.closest('[data-wishlist-add]') : null;
    if (add) { handleAddOne({ preventDefault: function () {}, stopPropagation: function () {}, currentTarget: add }); return; }
    // qty buttons
    if (e.target.closest && e.target.closest('[data-wishlist-qty-inc]')) { handleQtyBtn(e, +1); return; }
    if (e.target.closest && e.target.closest('[data-wishlist-qty-dec]')) { handleQtyBtn(e, -1); return; }
    // change variant on wishlist page
    var chgBtn = e.target.closest ? e.target.closest('[data-wishlist-change-variant]') : null;
    if (chgBtn) {
      e.preventDefault();
      e.stopPropagation();
      var handle = chgBtn.getAttribute('data-wishlist-handle');
      var item = load().find(function (i) { return i.handle === handle; });
      if (!item) return;
      fetchProduct(handle).then(function (p) {
        if (p && p.variants && p.variants.length > 1) openVariantPicker(item, p, 'change');
      });
      return;
    }
    // toggle heart (product cards + remove on wishlist page)
    var btn = e.target.closest ? e.target.closest('[data-wishlist-toggle]') : null;
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      var item = {
        handle: btn.getAttribute('data-wishlist-handle'),
        title: btn.getAttribute('data-wishlist-title') || '',
        url: btn.getAttribute('data-wishlist-url') || ('/products/' + btn.getAttribute('data-wishlist-handle')),
        image: btn.getAttribute('data-wishlist-image') || '',
        price: btn.getAttribute('data-wishlist-price') || '',
        brand: btn.getAttribute('data-wishlist-brand') || '',
        rating: btn.getAttribute('data-wishlist-rating') || '',
        ratingCount: btn.getAttribute('data-wishlist-rating-count') || '',
        variantId: btn.getAttribute('data-wishlist-variant-id') || null
      };
      if (!item.handle) return;
      // If already saved → remove without modal (works even for OOS items the user wants to clean up)
      if (has(item.handle)) { toggle(item); return; }
      // Skip modal for wishlist page (remove buttons) and whenever button doesn't carry variant data
      var inWishlistPage = !!btn.closest('#artmie-wishlist-page');
      if (inWishlistPage) { toggle(item); return; }
      // Block save if entire product is unavailable (out of stock)
      if (btn.getAttribute('data-wishlist-available') === 'false') {
        showToast('Производот е распродаден');
        return;
      }
      // Fetch product; if multiple variants → show picker (with availability check)
      fetchProduct(item.handle).then(function (p) {
        if (!p) { toggle(item); return; } // fail-open save if fetch fails
        var anyAvailable = (p.variants || []).some(function (v) { return v.available; });
        if (!anyAvailable) { showToast('Производот е распродаден'); return; }
        if (p.variants.length > 1) openVariantPicker(item, p);
        else toggle(item);
      }).catch(function () { toggle(item); });
    }
  }

  // Capture-phase mousedown/touchstart to beat wrapping <a> navigation
  function onEarly(e) {
    var btn = e.target.closest ? e.target.closest('[data-wishlist-toggle]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  }

  // ---------- utils ----------
  function renderStars(rating, count) {
    var r = parseFloat(rating);
    var c = parseInt(count, 10);
    if (!isFinite(r) || r <= 0 || !isFinite(c) || c <= 0) return '';
    var full = Math.round(r);
    var stars = '';
    for (var i = 1; i <= 5; i++) {
      stars += '<svg class="artmie-wl-star' + (i <= full ? ' is-on' : '') + '" viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.49L10 14.02l-4.94 2.68.94-5.49-4-3.9 5.53-.8z"/></svg>';
    }
    return stars + '<span class="artmie-wl-rating-count">(' + c + ')</span>';
  }

  function showToast(msg) {
    var existing = document.querySelector('.artmie-wl-toast');
    if (existing) existing.remove();
    var t = document.createElement('div');
    t.className = 'artmie-wl-toast';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(function () { t.classList.add('is-out'); }, 2200);
    setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 2700);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function cssEsc(s) { return String(s).replace(/["\\]/g, '\\$&'); }

  // ---------- wire up ----------
  document.addEventListener('mousedown', onEarly, true);
  document.addEventListener('touchstart', onEarly, { capture: true, passive: false });
  document.addEventListener('click', onClick, true);
  document.addEventListener('change', handleVariantChange);
  document.addEventListener('input', handleQtyInput);
  window.addEventListener('storage', function (e) { if (e.key === KEY) paint(); });
  document.addEventListener('shopify:section:load', function () { paint(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paint, { once: true });
  } else {
    paint();
  }
  // Re-paint on browser back/forward + when tab becomes visible
  window.addEventListener('pageshow', paint);
  document.addEventListener('visibilitychange', function () { if (!document.hidden) paint(); });
  // Safety pulse: theme morph (Horizon Idiomorph) can replace the header heart's badge
  // back to its server-rendered "0" placeholder. Paint() is idempotent (only touches DOM
  // when value differs) so this is cheap and self-correcting.
  /* ARTMIE_PERF_2026-07-07 — was setInterval(paint,1500) forever-poll; replaced with debounced MutationObserver on added wishlist nodes (covers load-more/section morphs; all other triggers already repaint). */
  (function(){
    var t=null;
    var mo=new MutationObserver(function(muts){
      for(var i=0;i<muts.length;i++){
        for(var j=0;j<muts[i].addedNodes.length;j++){
          var nd=muts[i].addedNodes[j];
          if(nd.nodeType===1&&((nd.hasAttribute&&nd.hasAttribute('data-wishlist-toggle'))||(nd.querySelector&&nd.querySelector('[data-wishlist-toggle]')))){
            clearTimeout(t); t=setTimeout(paint,300); return;
          }
        }
      }
    });
    function arm(){ mo.observe(document.body,{childList:true,subtree:true}); }
    if(document.body) arm(); else document.addEventListener('DOMContentLoaded',arm,{once:true});
  })();

  window.ArtmieWishlist = { load: load, toggle: toggle, has: has, paint: paint, addToCart: addToCart };
})();
